import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from './ui/resizable';
import { AlertTriangle, Plus, X, Save, Wand2, ExternalLink, FileText, ZoomIn, ZoomOut, ScanText, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { purchasesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const eur = (v) => (v == null ? '—'
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
// Même normalisation que le serveur (_reference_key) : les suggestions sont indexées ainsi
const refKey = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const parseAmount = (v) => {
  const n = parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};
const longDate = (d) => {
  try { return d ? format(parseISO(d), 'dd/MM/yyyy', { locale: fr }) : '—'; } catch { return d || '—'; }
};

const CATEGORY_OPTIONS = [
  ['chantier', 'Achat de chantier'], ['main-oeuvre', "Main-d'œuvre sous-traitée"],
  ['hors-chantier', 'Hors chantier'], ['a-classer', 'À classer'],
];
const ZOOM_STEPS = [100, 150, 200, 300];
const WIDE_SCREEN = '(min-width: 1024px)';

// Valeur du menu d'affectation <-> champs d'une ligne
const toChoice = (l) => {
  if (l.target === 'consumable') return 'consumable';
  if (l.target === 'stock') return 'stock';
  if (l.target === 'fees') return 'fees';
  if (l.target === 'prior') return 'prior';
  if (l.target === 'project') return l.projectId ? `project:${l.projectId}` : `legacy:${l.legacyChantierId}`;
  return '';
};
const fromChoice = (choice) => {
  if (choice === 'consumable') return { target: 'consumable', projectId: null, legacyChantierId: null };
  if (choice === 'stock') return { target: 'stock', projectId: null, legacyChantierId: null };
  if (choice === 'fees') return { target: 'fees', projectId: null, legacyChantierId: null };
  if (choice === 'prior') return { target: 'prior', projectId: null, legacyChantierId: null };
  if (choice.startsWith('project:')) return { target: 'project', projectId: choice.slice(8), legacyChantierId: null };
  if (choice.startsWith('legacy:')) return { target: 'project', projectId: null, legacyChantierId: choice.slice(7) };
  return { target: '', projectId: null, legacyChantierId: null };
};
const toEditable = (l) => ({ ...l, blDate: l.blDate || '', amountText: String(l.amountHT ?? '').replace('.', ','), choice: toChoice(l) });

const useWideScreen = () => {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_SCREEN).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE_SCREEN);
    const onChange = (e) => setWide(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return wide;
};

// Pages rendues en images par le serveur : le lecteur PDF du navigateur réduisait
// toute la page à la largeur de la colonne, et les scans devenaient illisibles.
const InvoicePreview = ({ purchase }) => {
  const [pages, setPages] = useState(null);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    purchasesAPI.pageCount(purchase.id)
      .then((r) => { if (!cancelled) setPages(r.pages); })
      .catch(() => { if (!cancelled) setPages(0); });
    return () => { cancelled = true; };
  }, [purchase.id]);

  const step = ZOOM_STEPS.indexOf(zoom);
  const renderWidth = zoom > 150 ? 2400 : 1600;

  return (
    <div className="flex flex-col h-full bg-gray-100">
      <div className="flex items-center gap-1 p-1 border-b border-gray-200 bg-white">
        <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => setZoom(100)} disabled={zoom === 100}>
          Ajuster à la largeur
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" title="Réduire"
                onClick={() => setZoom(ZOOM_STEPS[Math.max(0, step - 1)])} disabled={step <= 0}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="text-xs text-gray-600 w-12 text-center tabular-nums">{zoom} %</span>
        <Button type="button" size="sm" variant="outline" className="h-7 w-7 p-0" title="Agrandir"
                onClick={() => setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, step + 1)])} disabled={step >= ZOOM_STEPS.length - 1}>
          <ZoomIn className="w-4 h-4" />
        </Button>
        <a href={purchasesAPI.fileUrl(purchase.id)} target="_blank" rel="noopener noreferrer"
           className="ml-auto inline-flex items-center text-xs text-blue-700 hover:underline px-2">
          <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ouvrir le PDF
        </a>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-2 space-y-2">
        {pages === null && <p className="text-sm text-gray-500 p-2">Chargement de la facture…</p>}
        {pages === 0 && <p className="text-sm text-gray-500 p-2">Aperçu impossible : utilisez « Ouvrir le PDF ».</p>}
        {Array.from({ length: pages || 0 }, (_, i) => (
          <img key={`${i}-${renderWidth}`} src={purchasesAPI.pageImageUrl(purchase.id, i, renderWidth)}
               alt={`Page ${i + 1} de la facture`} loading={i < 2 ? 'eager' : 'lazy'} style={{ width: `${zoom}%` }}
               className="max-w-none block mx-auto bg-white shadow-sm" />
        ))}
      </div>
    </div>
  );
};

const PurchaseEditor = ({ purchase, context, isOpen, onOpenChange, onSaved }) => {
  const { toast } = useToast();
  const wide = useWideScreen();
  const [lines, setLines] = useState([]);
  const [category, setCategory] = useState(purchase.category);
  const [saving, setSaving] = useState(false);
  // Gardé ici plutôt que relu de la facture : la recharger effacerait les lignes
  // de BL en cours de saisie (l'éditeur les réinitialise à chaque changement).
  const [alerteVue, setAlerteVue] = useState(false);
  useEffect(() => { setAlerteVue(false); }, [purchase.id]);
  const alerteActive = Boolean(purchase.amountWarning) && !purchase.amountWarningAcknowledged && !alerteVue;

  const marquerAlerteVue = async () => {
    try {
      onSaved(await purchasesAPI.acknowledgeWarning(purchase.id));
      setAlerteVue(true);
    } catch (error) {
      toast({ title: "Impossible de marquer l'alerte", description: error?.response?.data?.detail || String(error),
              variant: 'destructive' });
    }
  };

  useEffect(() => {
    setLines((purchase.lines || []).map(toEditable));
    setCategory(purchase.category);
  }, [purchase]);

  const labels = useMemo(() => Object.fromEntries([
    ...context.projects.map((p) => [`project:${p.id}`, p.name]),
    ...context.legacyChantiers.map((c) => [`legacy:${c.id}`, `${c.name} (classeur)`]),
  ]), [context]);

  const nameIndex = useMemo(() => [
    ...context.projects.map((p) => ({ choice: `project:${p.id}`, words: new Set(refKey(`${p.name} ${p.clientName || ''}`).split(' ')) })),
    ...context.legacyChantiers.map((c) => ({ choice: `legacy:${c.id}`, words: new Set(refKey(c.name).split(' ')) })),
  ], [context]);

  // D'abord le chantier habituel de cette réf. commande, sinon un seul chantier dont le nom la contient
  const suggestFor = (reference) => {
    const key = refKey(reference);
    if (!key) return null;
    const known = context.referenceSuggestions[key];
    if (known && labels[known]) return known;
    const words = key.split(' ').filter((w) => w.length >= 4);
    const hits = nameIndex.filter((n) => words.some((w) => n.words.has(w)));
    return hits.length === 1 ? hits[0].choice : null;
  };

  // Ordre voulu par Samuel : en cours et en pause d'abord, puis les terminés du
  // plus récent au plus ancien — ceux dont on reçoit encore des BL —, et les
  // chantiers à venir en fin de liste.
  const option = (p) => <option key={p.id} value={`project:${p.id}`}>{p.name}</option>;
  const recent = (p) => p.actualEndDate || p.actualStartDate || '';
  const currentOptions = useMemo(() => context.projects
    .filter((p) => p.status === 'in-progress' || p.status === 'paused')
    .sort((a, b) => (b.actualStartDate || '').localeCompare(a.actualStartDate || '') || a.name.localeCompare(b.name, 'fr'))
    .map(option), [context.projects]);
  const plannedOptions = useMemo(() => context.projects
    .filter((p) => p.status === 'planned')
    .sort((a, b) => (a.actualStartDate || '9999').localeCompare(b.actualStartDate || '9999') || a.name.localeCompare(b.name, 'fr'))
    .map(option), [context.projects]);
  const completedOptions = useMemo(() => context.projects
    .filter((p) => p.status === 'completed')
    .sort((a, b) => recent(b).localeCompare(recent(a)) || a.name.localeCompare(b.name, 'fr'))
    .map(option), [context.projects]);
  const legacyOptions = useMemo(() => [...context.legacyChantiers]
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
    .map((c) => <option key={c.id} value={`legacy:${c.id}`}>{c.name}</option>), [context.legacyChantiers]);

  // Lignes lues automatiquement dans le PDF : une proposition, jamais un détail validé. Elle
  // ne remplit l'éditeur que tant qu'aucun détail n'est enregistré ; rien n'est sauvegardé
  // avant « Enregistrer le détail ». Le chantier habituel de la réf. est appliqué d'office.
  // `read` : la facture relue depuis l'ouverture de l'éditeur (la liste parente, elle, n'est
  // pas renvoyée à l'éditeur ouvert — même raison que `alerteVue`).
  const [read, setRead] = useState(null);
  useEffect(() => { setRead(null); }, [purchase.id]);
  const source = read || purchase;
  const proposed = !(purchase.lines || []).length && (source.suggestedLines || []).length > 0;
  const [reading, setReading] = useState(false);
  useEffect(() => {
    if (!proposed) return;
    setLines(source.suggestedLines.map((s) => ({
      id: null, blNumber: s.blNumber || '', blDate: s.blDate || '', reference: s.reference || '',
      amountText: String(s.amountHT ?? '').replace('.', ','),
      choice: s.target === 'fees' ? 'fees' : (suggestFor(s.reference) || ''),
    })));
    // eslint-disable-next-line
  }, [source, proposed]);

  const handleReadPdf = async () => {
    setReading(true);
    try {
      const updated = await purchasesAPI.autoRead(purchase.id);
      setRead(updated);
      onSaved(updated);
      if (!(updated.suggestedLines || []).length) {
        toast({ title: 'Aucun BL repéré', description: (updated.suggestion?.warnings || [])[0] || 'Format de facture non reconnu : détail à saisir à la main', duration: 7000 });
      }
    } catch (error) {
      toast({ title: 'Lecture impossible', description: error?.response?.data?.detail || String(error), variant: 'destructive' });
    } finally {
      setReading(false);
    }
  };

  const updateLine = (index, patch) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, { id: null, blNumber: '', blDate: '', reference: '', amountText: '', choice: '' }]);
  const removeLine = (index) => setLines((prev) => prev.filter((_, i) => i !== index));

  const suggestions = lines.map((l) => (l.choice ? null : suggestFor(l.reference)));
  const suggestionCount = suggestions.filter(Boolean).length;
  const applySuggestions = () => setLines((prev) => prev.map((l, i) => (!l.choice && suggestions[i] ? { ...l, choice: suggestions[i] } : l)));

  const totals = useMemo(() => {
    const amount = (l) => parseAmount(l.amountText) || 0;
    const sum = (list) => list.reduce((s, l) => s + amount(l), 0);
    const detailed = sum(lines);
    const assigned = sum(lines.filter((l) => l.choice.startsWith('project:') || l.choice.startsWith('legacy:')));
    const consumable = sum(lines.filter((l) => l.choice === 'consumable'));
    const stock = sum(lines.filter((l) => l.choice === 'stock'));
    const fees = sum(lines.filter((l) => l.choice === 'fees'));
    const prior = sum(lines.filter((l) => l.choice === 'prior'));
    return { detailed, assigned, consumable, stock, fees, prior,
             unassigned: detailed - assigned - consumable - stock - fees - prior,
             remainder: (purchase.amountHT || 0) - detailed };
  }, [lines, purchase.amountHT]);

  const invalid = lines.some((l) => parseAmount(l.amountText) === null);

  const handleCategory = async (value) => {
    const previous = category;
    setCategory(value);
    try {
      onSaved(await purchasesAPI.setCategory(purchase.id, value));
    } catch (error) {
      setCategory(previous);
      toast({ title: 'Classement impossible', description: error?.response?.data?.detail || String(error), variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = lines.map((l) => ({
        id: l.id || null, blNumber: l.blNumber || '', blDate: l.blDate || null,
        reference: l.reference || '', amountHT: parseAmount(l.amountText), ...fromChoice(l.choice),
      }));
      const updated = await purchasesAPI.saveLines(purchase.id, payload);
      onSaved(updated);
      toast({ title: 'Détail enregistré', description: `${purchase.supplierName} ${purchase.invoiceNumber} : ${updated.lines.length} BL`, duration: 3000 });
      onOpenChange(false);
    } catch (error) {
      toast({ title: "Échec de l'enregistrement", description: error?.response?.data?.detail || String(error), variant: 'destructive', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const editor = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label htmlFor="purchase-category" className="text-gray-600">Classement</label>
        <select id="purchase-category" value={category} onChange={(e) => handleCategory(e.target.value)}
                className="border border-gray-200 rounded px-2 py-1 bg-white">
          {CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {purchase.fileId && !(purchase.lines || []).length && (
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={handleReadPdf} disabled={reading}
                  title="Lit les BL dans le PDF et pré-remplit les lignes ; vous validez ensuite">
            {reading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ScanText className="w-3.5 h-3.5 mr-1" />}
            {reading ? 'Lecture…' : (source.suggestedLines || []).length ? 'Relire le PDF' : 'Lire le PDF'}
          </Button>
        )}
        {suggestionCount > 0 && (
          <Button size="sm" variant="outline" className={`h-7 ${purchase.fileId && !(purchase.lines || []).length ? '' : 'ml-auto'}`} onClick={applySuggestions}>
            <Wand2 className="w-3.5 h-3.5 mr-1" /> Appliquer {suggestionCount} suggestion{suggestionCount > 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {proposed && (
        <div className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
          <div className="flex items-start gap-2">
            <ScanText className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Lignes pré-remplies d'après le PDF{source.suggestion?.method === 'ocr' ? ' (facture scannée, lue par reconnaissance de texte)' : ''}.
              Vérifiez chaque ligne avec la facture, puis enregistrez : tant que ce n'est pas fait, rien n'est pris en compte.
            </span>
          </div>
          {(source.suggestion?.warnings || []).filter((w) => !w.startsWith('Facture scannée')).map((w) => (
            <div key={w} className="text-orange-800 pl-6">⚠ {w}</div>
          ))}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-600">
              <th className="px-2 py-1.5 font-medium">N° BL</th>
              <th className="px-2 py-1.5 font-medium">Date</th>
              <th className="px-2 py-1.5 font-medium">Réf. commande</th>
              <th className="px-2 py-1.5 font-medium text-right">Montant HT</th>
              <th className="px-2 py-1.5 font-medium">Affectation</th>
              <th className="w-6" />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">
                Aucun BL saisi : ajoutez-les en lisant la facture.
              </td></tr>
            )}
            {lines.map((l, i) => (
              <tr key={l.id || `nouveau-${i}`} className="border-t border-gray-100 align-top">
                <td className="px-1 py-1">
                  <Input aria-label="N° BL" value={l.blNumber} onChange={(e) => updateLine(i, { blNumber: e.target.value })} className="h-8 w-24" />
                </td>
                <td className="px-1 py-1">
                  <Input aria-label="Date du BL" type="date" value={l.blDate} onChange={(e) => updateLine(i, { blDate: e.target.value })} className="h-8 w-36" />
                </td>
                <td className="px-1 py-1">
                  <Input aria-label="Réf. commande" value={l.reference} onChange={(e) => updateLine(i, { reference: e.target.value })} className="h-8 w-28" />
                </td>
                <td className="px-1 py-1">
                  <Input aria-label="Montant HT" inputMode="decimal" value={l.amountText}
                         onChange={(e) => updateLine(i, { amountText: e.target.value })}
                         className={`h-8 w-24 text-right ${parseAmount(l.amountText) === null ? 'border-red-400' : ''}`} />
                </td>
                <td className="px-1 py-1">
                  <select aria-label="Affectation" value={l.choice} onChange={(e) => updateLine(i, { choice: e.target.value })}
                          className="h-8 border border-gray-200 rounded px-1 bg-white w-48">
                    <option value="">Non affecté</option>
                    <option value="consumable">Consommable</option>
                    <option value="stock">Stock</option>
                    <option value="fees">Frais de facturation</option>
                    <option value="prior">Ancien chantier (avant planning)</option>
                    {currentOptions.length > 0 && <optgroup label="En cours et en pause">{currentOptions}</optgroup>}
                    {completedOptions.length > 0 && <optgroup label="Terminés (du plus récent au plus ancien)">{completedOptions}</optgroup>}
                    {plannedOptions.length > 0 && <optgroup label="À venir">{plannedOptions}</optgroup>}
                    {legacyOptions.length > 0 && <optgroup label="Anciens chantiers (classeur)">{legacyOptions}</optgroup>}
                  </select>
                  {suggestions[i] && (
                    <button type="button" onClick={() => updateLine(i, { choice: suggestions[i] })}
                            className="block text-xs text-blue-700 hover:underline mt-0.5 text-left">
                      → {labels[suggestions[i]]}
                    </button>
                  )}
                </td>
                <td className="px-1 py-1">
                  <button type="button" onClick={() => removeLine(i)} title="Retirer ce BL"
                          className="text-red-500 hover:text-red-700 mt-2">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addLine}>
        <Plus className="w-4 h-4 mr-1" /> Ajouter un BL
      </Button>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm bg-gray-50 rounded-lg p-3">
        <span className="text-gray-600">Somme des BL</span>
        <span className="text-right tabular-nums">{eur(totals.detailed)}</span>
        <span className="text-gray-600">Reste à détailler</span>
        <span className={`text-right tabular-nums ${Math.abs(totals.remainder) >= 1 ? 'text-orange-700 font-medium' : 'text-green-700'}`}>
          {eur(totals.remainder)}
        </span>
        <span className="text-gray-600">Affecté aux chantiers</span>
        <span className="text-right tabular-nums">{eur(totals.assigned)}</span>
        <span className="text-gray-600">Consommables</span>
        <span className="text-right tabular-nums">{eur(totals.consumable)}</span>
        <span className="text-gray-600">Stock</span>
        <span className="text-right tabular-nums">{eur(totals.stock)}</span>
        <span className="text-gray-600">Frais de facturation</span>
        <span className="text-right tabular-nums">{eur(totals.fees)}</span>
        <span className="text-gray-600">Anciens chantiers</span>
        <span className="text-right tabular-nums">{eur(totals.prior)}</span>
        <span className="text-gray-600">Non affecté</span>
        <span className={`text-right tabular-nums ${Math.abs(totals.unassigned) >= 0.01 ? 'text-orange-700 font-medium' : ''}`}>
          {eur(totals.unassigned)}
        </span>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer sans enregistrer</Button>
        <Button onClick={handleSave} disabled={saving || invalid} className="bg-gray-800 hover:bg-gray-900 text-white">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer le détail'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[98vw] max-w-[98vw] h-[96vh] max-h-[96vh] bg-white p-4 flex flex-col gap-3">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-baseline gap-x-2 text-gray-800">
            <FileText className="w-5 h-5 self-center" />
            {purchase.supplierName} — facture {purchase.invoiceNumber}
            <span className="text-sm font-normal text-gray-500">
              du {longDate(purchase.invoiceDate)} · {eur(purchase.amountHT)} HT · {eur(purchase.amountTTC)} TTC
            </span>
          </DialogTitle>
        </DialogHeader>

        {alerteActive && (
          <div className="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded p-2 flex flex-wrap items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">{purchase.amountWarning} — à vérifier avec le comptable.</span>
            <Button size="sm" variant="outline" onClick={marquerAlerteVue}
                    className="border-orange-300 text-orange-800 bg-white hover:bg-orange-100"
                    title="L'alerte disparaît des listes ; elle reviendra si la situation change chez Pennylane">
              Marquer comme vue
            </Button>
          </div>
        )}
        {purchase.amountWarning && !alerteActive && (
          <p className="text-xs text-gray-400">
            Alerte vue : {purchase.amountWarning}.
          </p>
        )}

        <div className="flex-1 min-h-0">
          {purchase.fileId ? (
            // Poignée réglable : la largeur donnée à la facture est retenue d'une ouverture à l'autre
            <ResizablePanelGroup direction={wide ? 'horizontal' : 'vertical'} autoSaveId={`facture-bl-${wide ? 'large' : 'etroit'}`}
                                 className="rounded border border-gray-200">
              <ResizablePanel defaultSize={60} minSize={25}>
                <InvoicePreview purchase={purchase} />
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={40} minSize={25}>
                <div className="h-full overflow-auto p-3">{editor}</div>
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <div className="h-full overflow-auto space-y-3">
              {purchase.legacyLink ? (
                <a href={purchase.legacyLink} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center text-blue-700 hover:underline text-sm">
                  <ExternalLink className="w-4 h-4 mr-1" /> Ouvrir la facture (Drive)
                </a>
              ) : (
                <p className="text-sm text-gray-500">Pas de PDF pour cette facture.</p>
              )}
              {editor}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseEditor;
